<template>
  <div class="min-h-screen bg-gray-50">
    <HeaderPrincipal :show-user-type="true" />

    <!-- Contenido principal -->
    <div class="relative z-10 container mx-auto px-4 lg:px-8 py-8">
      <!-- Sección de bienvenida -->
      <div class="max-w-6xl mx-auto mb-12">
        <!-- Saludo personalizado -->
        <div class="saludo-container mb-8">
          <div class="saludo-badge inline-block px-8 py-6 rounded-2xl shadow-xl text-white font-bold backdrop-blur-sm"
               :style="{ background: $store.getters.colorPlan }">
            <span class="text-4xl md:text-5xl lg:text-6xl">Bienvenido/a</span>
            <div class="resaltar-fondo inline-block ml-4 bg-black/20 backdrop-blur-sm rounded-xl px-4 py-2">
              <span class="text-4xl md:text-5xl lg:text-6xl font-black">
                {{ $store.getters.nombre.split(' ')[0] }}
              </span>
            </div>
            <div class="intro-badge inline-block max-w-2xl px-6 py-4 rounded-xl shadow-lg text-white font-semibold text-xl md:text-2xl leading-relaxed"
                 :style="{ background: $store.getters.colorPlan }">
              Aquí comienza tu proceso de matrícula oficial para el programa:
            </div>
          </div>
        </div>

        <!-- Información del programa -->
        <div class="carrera-container relative bg-white rounded-3xl shadow-2xl p-8 md:p-12 mb-10 overflow-hidden">
          <img class="olitas-decoracion absolute left-4 top-8 w-16 md:w-20 opacity-60"
               src="@/assets/img/olas_verdes.png"
               alt="Decoración olas">
          <div class="relative z-10 ml-8 md:ml-12">
            <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-azul-institucional mb-2">
              <span class="text-verde-institucional">{{ $store.getters.carrera }}</span>
              <span class="text-azul-institucional"> Universidad de Aysén</span>
            </h2>
            <p class="text-xl md:text-2xl text-gray-600 font-medium">
              Departamento de {{ $store.getters.depto }}
            </p>
          </div>
          <div class="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-verde-institucional/10 to-transparent rounded-tl-full"></div>
        </div>
      </div>

      <!-- Proceso de matrícula -->
      <div class="max-w-4xl mx-auto">
        <div class="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <form @submit.prevent="siguiente" class="space-y-8">
            <!-- Título -->
            <div class="text-center mb-10">
              <h3 class="text-3xl font-bold text-azul-institucional mb-4">
                Proceso de Matrícula Oficial
              </h3>
              <p class="text-gray-600 text-lg leading-relaxed">
                Para formalizar su incorporación al programa, es necesario completar los siguientes pasos obligatorios
              </p>
            </div>

            <!-- Información importante -->
            <div class="bg-blue-50 border-l-4 border-azul-institucional rounded-r-lg p-6 mb-8">
              <div class="flex items-start space-x-3">
                <svg class="w-6 h-6 text-azul-institucional mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <div>
                  <h4 class="font-semibold text-azul-institucional mb-2">Información importante</h4>
                  <p class="text-gray-700 text-sm leading-relaxed">
                    La aceptación de estos términos es un requisito obligatorio para proceder con la matrícula.
                  </p>
                </div>
              </div>
            </div>

            <!-- Checkboxes -->
            <div class="space-y-6">
              <!-- 1. Aceptación de vacante -->
              <div class="bg-gray-50 rounded-2xl p-6 md:p-8 border-2 border-dashed border-gray-300 hover:border-verde-institucional transition-colors duration-300">
                <label class="flex items-start cursor-pointer group">
                  <div class="relative flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      v-model="aceptoVacante"
                      class="sr-only"
                    >
                    <div class="checkbox-custom w-6 h-6 border-2 border-gray-400 rounded-md transition-all duration-200 group-hover:border-verde-institucional"
                         :class="{ 'bg-verde-institucional border-verde-institucional': aceptoVacante }">
                      <svg v-if="aceptoVacante" class="w-4 h-4 text-white m-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <div class="ml-4">
                    <div class="text-lg md:text-xl text-azul-institucional font-semibold mb-2">
                      1. Aceptación de vacante
                    </div>
                    <p class="text-gray-700 leading-relaxed">
                      Acepto formalmente la vacante asignada para estudiar en el programa
                      <span class="font-bold text-verde-institucional">{{ $store.getters.carrera }}</span>
                      de la Universidad de Aysén.
                    </p>
                  </div>
                </label>
              </div>

              <!-- 2. Términos y condiciones -->
              <div class="bg-gray-50 rounded-2xl p-6 md:p-8 border-2 border-dashed border-gray-300 hover:border-verde-institucional transition-colors duration-300">
                <label class="flex items-start cursor-pointer group">
                  <div class="relative flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      v-model="aceptoTerminos"
                      class="sr-only"
                    >
                    <div class="checkbox-custom w-6 h-6 border-2 border-gray-400 rounded-md transition-all duration-200 group-hover:border-verde-institucional"
                         :class="{ 'bg-verde-institucional border-verde-institucional': aceptoTerminos }">
                      <svg v-if="aceptoTerminos" class="w-4 h-4 text-white m-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <div class="ml-4 flex-1">
                    <div class="text-lg md:text-xl text-azul-institucional font-semibold mb-2">
                      2. Términos y condiciones del programa
                    </div>
                    <p class="text-gray-700 leading-relaxed mb-4">
                      He leído, entendido y acepto los términos y condiciones específicos del programa.
                    </p>

                    <!-- Botón para ver PDF -->
                    <button
                      type="button"
                      @click="abrirTerminosCondiciones"
                      class="inline-flex items-center px-4 py-2 bg-azul-institucional hover:bg-verde-institucional text-white font-medium text-sm rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
                    >
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                      Ver términos y condiciones (PDF)
                    </button>

                    <!-- Indicador de visualización -->
                    <div v-if="pdfVisualizado" class="mt-3 flex items-center text-sm text-verde-institucional">
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      Documento visualizado
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <!-- Botón de envío -->
            <div class="flex flex-col items-end space-y-4 pt-8">
              <button
                type="submit"
                :disabled="!puedeAvanzar || cargando"
                class="px-8 py-4 bg-celeste-institucional hover:bg-azul-institucional disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold text-xl rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 min-w-48"
              >
                <span v-if="!cargando" class="flex items-center">
                  <span class="mr-2">Continuar con matrícula</span>
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                  </svg>
                </span>
                <span v-else class="flex items-center">
                  <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Procesando...
                </span>
              </button>

              <!-- Mensaje de validación -->
              <div v-if="!puedeAvanzar && (aceptoVacante || aceptoTerminos)" class="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg max-w-md">
                <div class="flex items-center">
                  <svg class="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                  </svg>
                  <span class="text-yellow-700 font-medium text-sm">
                    Debe aceptar ambos términos para continuar
                  </span>
                </div>
              </div>

              <!-- Mensaje de error -->
              <div v-if="error" class="bg-red-50 border-l-4 border-rojo-obstetricia p-4 rounded-lg max-w-md">
                <div class="flex items-start">
                  <svg class="w-5 h-5 text-rojo-obstetricia mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                  </svg>
                  <span class="text-rojo-obstetricia font-medium text-sm">{{ error }}</span>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="mt-16 pb-8">
      <ProgresoPasos :paso="1" />
    </div>

    <!-- Modal simple para PDF -->
    <modal
      name="terminos-modal"
      :adaptive="true"
      :scrollable="true"
      width="90%"
      height="90%"
      v-model="modalAbierto"
    >
      <div class="modal-container bg-white rounded-lg overflow-hidden shadow-2xl">
        <!-- Header -->
        <div class="bg-azul-institucional text-white p-4 flex justify-between items-center">
          <h3 class="text-lg font-semibold">Términos y Condiciones del Diplomado</h3>
          <div class="flex space-x-2">
            <button
              @click="abrirEnNuevaPestana"
              class="text-white hover:text-gray-200 p-1"
              title="Abrir en nueva pestaña"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
              </svg>
            </button>
            <button @click="cerrarModal" class="text-white hover:text-gray-200">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Contenido -->
        <div class="p-4" style="height: calc(90vh - 200px);">
          <div v-if="!pdfCargado" class="flex items-center justify-center h-full">
            <div class="text-center">
              <svg class="animate-spin h-8 w-8 text-azul-institucional mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="text-gray-600">Cargando documento...</p>
            </div>
          </div>

          <iframe
            v-show="pdfCargado"
            :src="urlPdf"
            class="w-full h-full border-0 rounded"
            title="Términos y Condiciones"
            @load="onPdfLoad"
          ></iframe>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 p-4 flex justify-end space-x-2 border-t">
          <button
            @click="abrirEnNuevaPestana"
            class="px-4 py-2 text-azul-institucional border border-azul-institucional rounded hover:bg-gray-100"
          >
            Abrir en nueva pestaña
          </button>
          <button
            @click="cerrarModal"
            class="px-4 py-2 bg-azul-institucional text-white rounded hover:bg-verde-institucional"
          >
            Cerrar
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import HeaderPrincipal from '@/componentes/UI/HeaderPrincipal.vue';
import ProgresoPasos from '@/componentes/ProgresoPasos.vue';
import {
  aceptarTerminosCondicionesDiplomados,
  aceptarVacanteEducacionContinua
} from '@/assets/api/matricula';

export default {
  name: 'Bienvenida',
  components: {
    HeaderPrincipal,
    ProgresoPasos
  },
  data() {
    return {
      aceptoVacante: false,
      aceptoTerminos: false,
      cargando: false,
      error: '',
      modalAbierto: false,
      pdfCargado: false,
      pdfVisualizado: false,
      // Reemplaza FILE_ID con tu ID real de Google Drive
      urlPdf: 'https://drive.google.com/file/d/1IyHkb84zTKxftEmW6EwV8syiHGyXIcU1/preview'
    };
  },
  computed: {
    puedeAvanzar() {
      return this.aceptoVacante && this.aceptoTerminos;
    }
  },
  methods: {
    abrirTerminosCondiciones() {
      this.$modal.show('terminos-modal');
      this.pdfCargado = false;
      this.pdfVisualizado = true;
    },

    cerrarModal() {
      this.$modal.hide('terminos-modal');
    },

    onPdfLoad() {
      this.pdfCargado = true;
    },

    abrirEnNuevaPestana() {
      const directUrl = this.urlPdf.replace('/preview', '/view?usp=sharing');
      window.open(directUrl, '_blank');
      this.pdfVisualizado = true;
    },

    async siguiente() {
      if (!this.puedeAvanzar) return;

      this.cargando = true;
      this.error = '';

      try {
        // Ejecutar ambas APIs en paralelo
        const [respuestaVacante, respuestaTerminos] = await Promise.all([
          aceptarVacanteEducacionContinua(this.$store.state.idPostulante),
          aceptarTerminosCondicionesDiplomados(this.$store.state.idPostulante)
        ]);

        // Solo actualizar si ambas fueron exitosas
        this.$store.commit('actualizarEstudiante', respuestaVacante.estudiante);
        this.$store.commit('actualizarEtapa', 1);

        this.$nextTick(() => {
          this.$router.push({ name: 'perfil estudiante educontinua' });
        });

      } catch (error) {
        this.cargando = false;
        // Mostrar el mensaje específico del error
        this.error = error.message || 'Ha ocurrido un error al procesar su matrícula. Por favor intente nuevamente.';
      }
    }
  }
};
</script>

<style scoped>
.text-azul-institucional { color: var(--azul-institucional); }
.text-verde-institucional { color: var(--verde-institucional); }
.text-rojo-obstetricia { color: var(--rojo-obstetricia); }
.bg-celeste-institucional { background-color: var(--celeste-institucional); }
.bg-azul-institucional { background-color: var(--azul-institucional); }
.bg-verde-institucional { background-color: var(--verde-institucional); }
.border-verde-institucional { border-color: var(--verde-institucional); }
.border-rojo-obstetricia { border-color: var(--rojo-obstetricia); }
.border-azul-institucional { border-color: var(--azul-institucional); }
.hover\:bg-azul-institucional:hover { background-color: var(--azul-institucional); }
.hover\:bg-verde-institucional:hover { background-color: var(--verde-institucional); }
.hover\:border-verde-institucional:hover { border-color: var(--verde-institucional); }

.checkbox-custom {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.modal-container {
  max-width: 90vw;
  max-height: 90vh;
}

@media (max-width: 768px) {
  .saludo-badge span {
    font-size: 2.5rem !important;
  }
}
</style>
